import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { Link } from "@tanstack/react-router"
import { Button as ButtonLib } from "@ui-lib/index"
import { CDN_URL, LOGOS_PATH } from "@utils/cdn"
import { ReactElement } from "react"

const getComponents = (companyName: string | undefined): [ReactElement, string] => {
	const buttonsList: { [companyKey: string]: ReactElement } = {}
	const iconsList: { [companyKey: string]: string } = {}

	const fallbackButton: ReactElement = (
		<ButtonLib
			variant={"filled-primary"}
			className={`w-33.75`}
		>
			<ButtonLib.Label>{"Go to Home"}</ButtonLib.Label>
		</ButtonLib>
	)
	const fallbackIcon = `${CDN_URL}/icons/BrokenLinkIcon.svg`
	return [
		buttonsList[companyName !== undefined ? companyName : "no company"] ?? fallbackButton,
		iconsList[companyName !== undefined ? companyName : "no company"] ?? fallbackIcon,
	]
}

const getCustomButtonByCompanyName = (
	companyName: string | undefined,
	label: string
): ReactElement => {
	const buttonsList: { [companyKey: string]: ReactElement } = {}
	const fallbackButton: ReactElement = (
		<ButtonLib
			variant={"filled-primary"}
			className={`w-33.75 px-2`}
		>
			<ButtonLib.Label>{label}</ButtonLib.Label>
		</ButtonLib>
	)
	return buttonsList[companyName !== undefined ? companyName : "no company"] ?? fallbackButton
}

type NotFoundPageColorsProp = {
	bg: string
}

const getCompanyColorsByCompanyName = (companyName: string | undefined): NotFoundPageColorsProp => {
	const componentColorsList: { [companyKey: string]: NotFoundPageColorsProp } = {
		Sølvtrans: { bg: "bg-gray-666" },
	}
	const componentColorsFallback: NotFoundPageColorsProp = {
		bg: "bg-dipai-secondary-901",
	}
	return (
		componentColorsList[companyName !== undefined ? companyName : "no company"] ??
		componentColorsFallback
	)
}

export default function NotFoundPage({
	linkTo,
	labelTo,
}: {
	linkTo?: string
	labelTo?: string
}): ReactElement {
	const companyName = useAppSelector(getCurrentCompany)
	return (
		<div className={`h-[100vh] ${getCompanyColorsByCompanyName(companyName).bg} text-white`}>
			<div className="grid h-24 grid-cols-2">
				<img
					className="mt-9 ml-12 h-7"
					src={`${CDN_URL}/${LOGOS_PATH}/dipai.png`}
					alt="Dipai AS"
				/>
				<div className="relative">
					<div className="text-tiny absolute right-12 mt-12">
						Problems? Please contact <span className="underline">Dipai Support</span>.
					</div>
				</div>
			</div>

			<div className="m-auto mt-[calc(50vh-96px-260px)] flex w-112.5 flex-col items-center">
				<img
					className="mb-8 w-25"
					src={getComponents(companyName)[1]}
					alt=""
				/>
				<p className="text-title2 mb-5 h-12.25 text-center">This Page Isn't Available</p>
				<p className="text-subheadline m-auto mb-8 text-center">
					{" "}
					The link may be broken, or the page may have been removed. Check to see if the
					link you're trying to open is correct.{" "}
				</p>
				<Link to={linkTo !== undefined ? linkTo : "/"}>
					{labelTo !== undefined
						? getCustomButtonByCompanyName(companyName, labelTo)
						: getComponents(companyName)[0]}
				</Link>
			</div>
		</div>
	)
}

export function NotFoundPageNL({
	linkTo,
	labelTo,
}: {
	linkTo?: string
	labelTo?: string
}): ReactElement {
	const companyName = useAppSelector(getCurrentCompany)
	return (
		<div className={`h-[100vh] ${getCompanyColorsByCompanyName(companyName).bg} text-white`}>
			<div className="text-button flex h-13 flex-row items-center justify-end pr-5">
				<p>
					Problems? Please contact <span className="underline">Dipai Support</span>.
				</p>
			</div>

			<div className="m-auto mt-[calc((100vh-52px)/2-155px)] flex w-112.5 flex-col items-center">
				<img
					className="mb-8 w-25"
					src={getComponents(companyName)[1]}
					alt=""
				/>
				<p className="text-title2 mb-5 h-12.25 text-center">This Page Isn't Available</p>
				<p className="text-subheadline m-auto mb-8 text-center">
					{" "}
					The link may be broken, or the page may have been removed. Check to see if the
					link you're trying to open is correct.{" "}
				</p>
				<Link to={linkTo !== undefined ? linkTo : "/"}>
					{labelTo !== undefined
						? getCustomButtonByCompanyName(companyName, labelTo)
						: getComponents(companyName)[0]}
				</Link>
			</div>
		</div>
	)
}
